<template>
  <div class="section">
    <h4 class="section-title">
      {{ $t('Device Management') }}
    </h4>
    <p class="divider small"></p>
    <div class="device-tips" v-html="selectTitleDesc" @click="handleGotoSettings"></div>
    <div class="overflow-x-auto" ref="refTableParent">
      <table ref="refTable">
        <thead>
          <tr>
            <th>{{ $t('Device Name') }}</th>
            <th>{{ $t('Last active') }}</th>
            <th>{{ $t('Location') }}</th>
            <th class="align-center sticky">{{ $t('Manage') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!list.length">
            <td class="empty" colspan="4" v-loading="tableLoading">{{ $t('No data') }}</td>
          </tr>
          <template v-else>
            <tr v-for="(item, index) in list" :key="item.id">
              <td>
                <div :class="`device-type ${getDeviceTypeClassName(item.deviceType)}`">
                  {{ item.deviceName }}
                </div>
              </td>
              <td>{{ formatTime(item.activeTime * 1000, 'LLL') }}</td>
              <td>{{ appStore.isCN ? [item.country, item.state, item.city].join(' • ') : item.ip }}</td>
              <td class="align-center sticky">
                <el-button size="small" @click="handleLogout(item, index)">{{ $t('Login out') }}</el-button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { getCurrentInstance, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getDeviceList, logOutDevice } from '@/api/user/user';
import { formatTime } from '@/libs/tools';
import { ElMessage, ElMessageBox } from 'element-plus';
import { appStore, userStore } from '@/stores';
import useTableSticked from '@/view/user/center/hooks/useTableSticked';
const { $t } = getCurrentInstance().proxy
const $router = useRouter()

// 跳转到设置页面
const selectTitleDesc = !userStore.canChangePassword ? $t('All devices that have permission to log in to this account will be displayed here. If you do not want a device to continue logging into the account, you can remove the device in time.')
                                                 : $t('All devices that have permission to log in to this account will be displayed here. If you do not want a device to continue logging into the account, you can remove the device in time and <a href="#" class="change-pass">change the password</a>.')
const handleGotoSettings = event => {
  event.preventDefault()
  if (event.target.classList.contains('change-pass')) {
    $router.push({
      name: 'accountCenterSettings'
    })
  }
}

// 获取登录设备列表
const tableLoading = ref(false)
const list = reactive([])
const getList = async () => {
  if (tableLoading.value) return
  tableLoading.value = true
  try {
    const { data } = await getDeviceList()
    list.splice(0, list.length)
    list.push(...data.list)
  } catch (e) {
    console.log(e)
  }
  tableLoading.value = false
}

// 退出设备
const handleLogout = async (item, index) => {
  ElMessageBox.confirm($t('After being removed, the device will automatically log out and needs to be logged in again. Do you confirm the operation?'), $t('Are you sure?'), {
    confirmButtonText: $t('Yes'),
    cancelButtonText: $t('No'),
    center: true,
    beforeClose: async (action, instance, done) => {
      if (action === 'confirm') {
        instance.confirmButtonLoading = true
        try {
          await logOutDevice(item.id)
          ElMessage({
            type: 'success',
            message: $t('Saved success'),
          })
          list.splice(index, 1)
          done()
        } catch (e) {
          console.log(e)
        }
        instance.confirmButtonLoading = false
      } else {
        done()
      }
    }
  })
}

// 获取设备图标类名
const getDeviceTypeClassName = deviceType => {
  if (/^win/i.test(deviceType)) {
    return 'win'
  } else if (/^mac/i.test(deviceType)) {
    return 'mac'
  } else if (/^an/i.test(deviceType)) {
    return 'android'
  } else if (/^ios/i.test(deviceType)) {
    return 'ios'
  } else if (/^ip/i.test(deviceType)) {
    return 'pad'
  } else {
    return 'mac'
  }
}

const refTableParent = ref()
const refTable = ref()
useTableSticked(refTableParent, refTable)

onMounted(() => {
  getList()
})
</script>

<style lang="scss" scoped>
table {
  min-width: 550px;
  th, td {
    padding: 14px 16px;
  }
}
.device-type {
  display: flex;
  align-items: center;
  &::before {
    content: '';
    display: block;
    width: 32px;
    height: 32px;
    margin-right: 12px;
    background-image: url(@/assets/icon-device-unknown.svg);
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  }
  &.mac, &.win {
    &::before {
      background-image: url(@/assets/icon-device-pc.svg);
    }
  }
  &.ios, &.android {
    &::before {
      background-image: url(@/assets/icon-device-mobile.svg);
    }
  }
  &.pad {
    &::before {
      background-image: url(@/assets/icon-device-pad.svg);
    }
  }
}
.device-tips {
  color: var(--BG-9, #474747);
  font-size: 14px;
  line-height: 20px;
  padding: 16px;
  background: var(--BG-2, #EAEAEA);
  border-radius: 12px;
  margin-bottom: 24px;
  :deep(a) {
    color: inherit;
  }
}
.ar-SA {
  .device-type {
    &::before {
      margin-left: 12px;
      margin-right: 0;
    }
  }
}
</style>